// src/encounter/ips-occupation-dialog.tsx
// Add/Edit dialog for the rail's Occupation card (PROD-1272). PRESENTATIONAL — composes DS Modal +
// TextField (Job role, Employment) + TextArea (Occupational information). Mirrors Flutter
// occupation_form.dart / add_edit_occupation_widget.dart. No hooks/network: the host supplies
// `existing` (pre-fill + edit-vs-add title) and the submit/close callbacks; on submit it emits an
// OccupationFormValue which the rail turns into a wire body via buildOccupationRequest +
// useSaveOccupation.
import { useState } from 'react';
import { TextField, TextArea, HelperText } from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import type { PatientOccupation } from '@/types/patient-detail-api';
import type { OccupationFormValue } from '@/encounter/lib/to-occupation-request';

export interface IpsOccupationDialogProps {
  open: boolean;
  /** Existing entity → Edit (pre-filled); null/empty → Add. */
  existing?: PatientOccupation | null;
  saving?: boolean;
  onSubmit: (value: OccupationFormValue) => void;
  onClose: () => void;
}

const FIELD_GROUP = 'mb-4';

function hasOccupation(o: PatientOccupation | null | undefined): boolean {
  return !!(o && (o.jobRole?.trim() || o.employment?.trim() || o.occupationalInformation?.trim()));
}

/** Outer gate + keyed remount so each open re-initialises from `existing` via lazy useState (no
 *  setState-in-effect). */
export function IpsOccupationDialog(props: IpsOccupationDialogProps) {
  if (!props.open) return null;
  return <IpsOccupationDialogForm key={props.existing?.id ?? 'add'} {...props} />;
}

function IpsOccupationDialogForm({ existing = null, saving, onSubmit, onClose }: IpsOccupationDialogProps) {
  const isEditing = hasOccupation(existing);
  const [jobRole, setJobRole] = useState(() => existing?.jobRole ?? '');
  const [employment, setEmployment] = useState(() => existing?.employment ?? '');
  const [info, setInfo] = useState(() => existing?.occupationalInformation ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    if (!jobRole.trim() && !employment.trim() && !info.trim()) {
      setError('Enter at least one occupation field.');
      return;
    }
    onSubmit({ jobRole, employment, occupationalInformation: info });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit occupation' : 'Add occupation'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save occupation',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        <div className={FIELD_GROUP}>
          <TextField
            label="Job role"
            value={jobRole}
            onChange={(e) => { setJobRole(e.target.value); setError(null); }}
            placeholder="e.g. QA Engineer"
            disabled={saving}
          />
        </div>
        <div className={FIELD_GROUP}>
          <TextField
            label="Employment"
            value={employment}
            onChange={(e) => { setEmployment(e.target.value); setError(null); }}
            placeholder="e.g. Employed, Self-employed"
            disabled={saving}
          />
        </div>
        <div className={FIELD_GROUP}>
          <TextArea
            label="Occupational information"
            value={info}
            onChange={(e) => { setInfo(e.target.value); setError(null); }}
            placeholder="Optional — e.g. shift pattern, hazards"
            rows={3}
            disabled={saving}
          />
        </div>
        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
